import toast from "react-hot-toast";
import { Tag, Plus, Edit2, Trash2, Hash, Palette, Search } from "lucide-react";
import { useState } from "react";
import ConfirmModal from "../modal/ConfirmModal";
import TagModal from "../modal/TagModal";
import { useTags } from "../hooks/useTags";

const Tags = () => {
  const { tags, addTag, updateTag, deleteTag, loading, error } = useTags();

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  const filteredTags = tags.filter((tag) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = tag.name?.toLowerCase().includes(term);
    return nameMatch;
  });

  const openTagModal = () => {
    setSelectedTag(null);
    setIsModalOpen(true);
  };

  const handleSaveTag = async (tagData) => {
    try {
      if (selectedTag) {
        await updateTag(selectedTag.id, tagData);
        toast.success("Tag atualizada com sucesso!");
      } else {
        await addTag(tagData);
        toast.success("Tag adicionada com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar a tag.");
    }
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setIsModalOpen(true);
  };

  const handleDeleteTag = (tagId) => {
    setTagToDelete(tagId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (tagToDelete) {
        await deleteTag(tagToDelete);
        toast.success("Tag excluída com sucesso!");
        setTagToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir a tag.");
    }
  };

  if (loading) return <p>Carregando tags...</p>;
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestão de Tags
          </h1>
          <button
            onClick={openTagModal}
            className="rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
          >
            <Plus className="mr-2 inline h-4 w-4" />
            Nova Tag
          </button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Hash className="mx-auto mb-2 h-8 w-8 text-blue-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {tags.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total de Tags
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Tag className="mx-auto mb-2 h-8 w-8 text-green-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {tags.filter((t) => t.active).length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ativas</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Palette className="mx-auto mb-2 h-8 w-8 text-purple-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {new Set(tags.map((t) => t.backgroundColor)).size}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Cores Únicas
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Plus className="mx-auto mb-2 h-8 w-8 text-yellow-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {tags.reduce((sum, t) => sum + (t.usageCount || 0), 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Usos Totais
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-300 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Lista de Tags
            </h2>

            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cor de Fundo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cor do Texto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Usos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {filteredTags.map((tag) => (
                  <tr
                    key={tag.id}
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                        style={{
                          backgroundColor: tag.backgroundColor,
                          color: tag.textColor,
                        }}
                      >
                        {tag.name}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <Tag className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {tag.name}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div
                          className="mr-2 h-6 w-6 rounded border border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: tag.backgroundColor }}
                        ></div>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {tag.backgroundColor}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div
                          className="mr-2 h-6 w-6 rounded border border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: tag.textColor }}
                        ></div>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {tag.textColor}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {tag.usageCount || 0}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditTag(tag)}
                          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag.id)}
                          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TagModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTag}
        tag={selectedTag}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Tag"
        message="Tem certeza que deseja excluir esta tag? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default Tags;
